/**
 * The police going after the player off the road: a police car leaving the
 * traffic to drive straight at them across open ground, and officers out of
 * their cars on foot. Pure: `main.ts` moves them.
 *
 * - **A car off the road** turns no tighter than a car does (`TURNING`, its
 *   circle's radius), takes up speed and brakes as one, and is slower on
 *   rough ground. After someone in a vehicle it comes on faster than they
 *   go, to ram them, and pins them once they are all but stopped; after
 *   someone on foot it pulls up a few metres short,
 *   for its officers to get out. It slows for the turn it needs, and with
 *   the one it chases too near its side to turn to, drives on to come
 *   round again, rather than circling them.
 * - **An officer on foot** sprints (`SPRINT`, a little faster than the
 *   player runs) for `SPRINT_TIME` seconds, then runs on at `JOG`: a good
 *   head start gets away. Within `TACKLE` of someone on foot, they bring
 *   them down.
 */

/** An officer's sprint, m/s, and for how long, seconds; then their run. */
export const SPRINT = 7;
export const SPRINT_TIME = 10;
export const JOG = 5.2;
/** How near an officer must come to tackle someone on foot, and to reach someone in a vehicle, metres. */
export const TACKLE = 1.2;
export const AT_THE_DOOR = 2.6;

/** How fast an officer `distance` metres from the one they chase goes, with `sprint` seconds of sprint left: m/s. */
export function officerPace(distance: number, sprint: number): number {
  if (distance <= TACKLE * 0.8) return 0;
  // The last few metres at a walk if the one chased stands still: see `main.ts`.
  return sprint > 0 ? SPRINT : JOG;
}

/** A police car off the road: its turning circle's radius, metres; its top speed on a road and off one, m/s; how hard it speeds up and brakes, m/s². */
export const CHASER = { turning: 7, top: 26, roughTop: 16, accel: 5, brake: 9 } as const;

/** Turn from `yaw` toward `want` (radians), at `speed`, for `dt`: no tighter than its turning circle, and at a crawl not at all. */
export function chaserTurn(yaw: number, want: number, speed: number, dt: number): number {
  const most = Math.min(TURN_RATE, Math.abs(speed) / CHASER.turning) * dt;
  const off = Math.atan2(Math.sin(want - yaw), Math.cos(want - yaw));
  return yaw + Math.max(-most, Math.min(most, off));
}

/**
 * How fast a police car off the road goes next, from `speed`: `distance`
 * metres from the one it chases, who goes at `theirs` m/s, `onFoot` or not,
 * the car on `rough` ground or not.
 */
export function chaserSpeed(speed: number, distance: number, theirs: number, onFoot: boolean, rough: boolean, dt: number): number {
  const top = rough ? CHASER.roughTop : CHASER.top;
  // On foot: up to a few metres short, then stopped. In a vehicle: faster than them, into them.
  // On foot it brakes in time to stop short, a little gentler than it could.
  // At a vehicle all but stopped, up against it, it stops: pinned, not shoved along.
  const want = onFoot
    ? Math.min(top, Math.sqrt(2 * CHASER.brake * 0.7 * Math.max(0, distance - PULL_UP)))
    : distance < PINNED && Math.abs(theirs) < PIN_SPEED ? 0
      : distance > 15 ? top : Math.min(top, Math.abs(theirs) + 4);
  return want > speed ? Math.min(want, speed + CHASER.accel * dt) : Math.max(want, speed - CHASER.brake * dt);
}

/** How far short of someone on foot a police car pulls up, metres. */
export const PULL_UP = 7;
/** How near a vehicle going slower than `PIN_SPEED` (m/s) a police car stops against it, metres between them. */
export const PINNED = 6;
export const PIN_SPEED = 4;

/** A police car off the road: its heading (radians, scene yaw: forward is -z turned by it) and speed, m/s. */
export interface Chaser {
  yaw: number;
  speed: number;
}

/**
 * On by `dt`: a police car off the road after someone, going `theirs` m/s,
 * `onFoot` or not, on `rough` ground or not. It drives at the point
 * (`dx`, `dz`) from it in the scene, the next corner of its way to them
 * (see `findway.ts`), or them; `remaining` metres from them along its way.
 * Turns toward the point if it can make the turn, and goes no faster than
 * the turn allows.
 */
export function chaserStep(c: Chaser, dx: number, dz: number, theirs: number, onFoot: boolean, rough: boolean, dt: number, remaining = Math.hypot(dx, dz)): void {
  const d = Math.hypot(dx, dz);
  const want = Math.atan2(-dx, -dz);
  const off = Math.abs(Math.atan2(Math.sin(want - c.yaw), Math.cos(want - c.yaw)));
  // The circle through them it would have to drive: too tight, drive on.
  const needs = off < 1e-3 ? Infinity : d / (2 * Math.sin(off));
  const tooTight = off <= Math.PI / 2 && needs < CHASER.turning;
  const cap = TURN_RATE * (off > Math.PI / 2 || tooTight ? CHASER.turning : needs);
  if (!tooTight) c.yaw = chaserTurn(c.yaw, want, c.speed, dt);
  const target = chaserSpeed(c.speed, remaining, theirs, onFoot, rough, dt);
  c.speed = Math.min(target, Math.max(cap, c.speed - CHASER.brake * dt));
}

/** The fastest a police car off the road turns, rad/s. */
const TURN_RATE = 1.4;
